import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { X, Check } from "lucide-react";

/* ------------------------------ DATA ------------------------------ */
const GRID_TIE = [
  {
    kw: "3.6 kW",
    tag: "Grid Tie",
    price: "₱125,000",
    inclusions: [
      "6 pcs Mono 615W Solar Panel",
      "DEYE / SOLIS Inverter 3.6 kW",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "5.5 kW",
    tag: "Grid Tie",
    price: "₱160,000",
    popular: true,
    inclusions: [
      "9 pcs Mono 615W Solar Panel",
      "DEYE Inverter 5 kW",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "8.6 kW",
    tag: "Grid Tie",
    price: "₱250,000",
    inclusions: [
      "14 pcs Mono 615W Solar Panel",
      "DEYE Inverter 8 kW",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "10.45 kW",
    tag: "Grid Tie",
    price: "₱310,000",
    inclusions: [
      "17 pcs Mono 615W Solar Panel",
      "DEYE Inverter 10 kW",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
];

const HYBRID_100 = [
  {
    kw: "3.6 kW",
    tag: "Hybrid",
    price: "₱210,000",
    battery: "100Ah",
    inclusions: [
      "6 pcs Mono 615W Solar Panel",
      "DEYE Inverter 3.6 kW",
      "100Ah LiFePO₄ Battery 51.2V (SNRE)",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "5.5 kW",
    tag: "Hybrid",
    price: "₱265,000",
    battery: "100Ah",
    inclusions: [
      "9 pcs Mono 615W Solar Panel",
      "DEYE Inverter 5 kW",
      "100Ah LiFePO₄ Battery 51.2V (SNRE)",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "8.6 kW",
    tag: "Hybrid",
    price: "₱355,000",
    battery: "100Ah",
    inclusions: [
      "14 pcs Mono 615W Solar Panel",
      "DEYE Inverter 8 kW",
      "100Ah LiFePO₄ Battery 51.2V (SNRE)",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "10.45 kW",
    tag: "Hybrid",
    price: "₱445,000",
    battery: "100Ah",
    inclusions: [
      "17 pcs Mono 615W Solar Panel",
      "DEYE Inverter 10 kW",
      "100Ah LiFePO₄ Battery 51.2V (SNRE)",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "12.3 kW",
    tag: "Hybrid",
    price: "₱500,000",
    battery: "100Ah",
    inclusions: [
      "20 pcs Mono 615W Solar Panel",
      "DEYE Inverter 12 kW",
      "100Ah LiFePO₄ Battery 51.2V (SNRE)",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
];

const HYBRID_200 = [
  {
    kw: "3.6 kW",
    tag: "Hybrid",
    price: "₱240,000",
    battery: "200Ah",
    inclusions: [
      "6 pcs Mono 615W Solar Panel",
      "DEYE Inverter 3.6 kW",
      "200Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "5.5 kW",
    tag: "Hybrid",
    price: "₱295,000",
    battery: "200Ah",
    inclusions: [
      "9 pcs Mono 615W Solar Panel",
      "DEYE Inverter 5 kW",
      "200Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "8.6 kW",
    tag: "Hybrid",
    price: "₱395,000",
    battery: "200Ah",
    inclusions: [
      "14 pcs Mono 615W Solar Panel",
      "DEYE Inverter 8 kW",
      "200Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "10.45 kW",
    tag: "Hybrid",
    price: "₱465,000",
    battery: "200Ah",
    inclusions: [
      "17 pcs Mono 615W Solar Panel",
      "DEYE Inverter 10 kW",
      "200Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "12.3 kW",
    tag: "Hybrid",
    price: "₱530,000",
    battery: "200Ah",
    inclusions: [
      "20 pcs Mono 615W Solar Panel",
      "DEYE Inverter 12 kW",
      "200Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
];

const HYBRID_314 = [
  {
    kw: "5.5 kW",
    tag: "Hybrid",
    price: "₱320,000",
    battery: "314Ah",
    inclusions: [
      "9 pcs Mono 615W Solar Panel",
      "DEYE Inverter 5 kW",
      "314Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "8.6 kW",
    tag: "Hybrid",
    price: "₱415,000",
    battery: "314Ah",
    inclusions: [
      "14 pcs Mono 615W Solar Panel",
      "DEYE Inverter 8 kW",
      "314Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "10.45 kW",
    tag: "Hybrid",
    price: "₱495,000",
    battery: "314Ah",
    inclusions: [
      "17 pcs Mono 615W Solar Panel",
      "DEYE Inverter 10 kW",
      "314Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "12.3 kW",
    tag: "Hybrid",
    price: "₱560,000",
    battery: "314Ah",
    inclusions: [
      "20 pcs Mono 615W Solar Panel",
      "DEYE Inverter 12 kW",
      "314Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
];

/* ----------------------------- PRODUCT MODAL ----------------------------- */
function ProductModal({ open, onClose, pkg }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !pkg) return null;
  const isHybrid = pkg.tag?.toLowerCase().includes("hybrid");

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center font-inter"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      onClick={onClose}
    >
      <motion.div
        className="relative w-[min(92vw,900px)] max-h-[90svh] rounded-2xl bg-card text-text-primary overflow-hidden flex flex-col ring-1 ring-border-light shadow-2xl"
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 220, damping: 24 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
        >
          <X size={20} />
        </button>

        <div className="overflow-y-auto flex-1 space-y-8 p-8">
          {/* Header */}
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-start">
              <span className="rounded-lg bg-accent-sun/20 px-4 py-2 text-sm font-bold text-accent-sun border border-accent-sun/50">
                {pkg.tag}
              </span>
              {pkg.battery && (
                <span className="rounded-lg bg-accent-green/20 px-4 py-2 text-sm font-bold text-accent-green border border-accent-green/50">
                  🔋 {pkg.battery} Battery
                </span>
              )}
            </div>
            <h2 className="font-sharetech text-4xl md:text-5xl font-bold text-text-primary">{pkg.kw}</h2>
            <p className="font-sharetech text-3xl text-accent-sun font-bold">{pkg.price}</p>
          </div>

          {/* Inclusions */}
          <section className="space-y-4">
            <h3 className="font-bold text-lg font-sharetech text-text-primary">What's Included</h3>
            <ul className="space-y-3">
              {pkg.inclusions.map((item) => (
                <li key={item} className="flex gap-3 items-start">
                  <Check size={20} className="text-accent-green flex-shrink-0 mt-0.5" />
                  <span className="text-text-body">{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Warranties */}
          <section className="space-y-4">
            <h3 className="font-bold text-lg font-sharetech text-text-primary">Warranties</h3>
            <ul className="space-y-2 text-text-body">
              <li className="flex gap-3 items-start">
                <Check size={18} className="text-accent-sun flex-shrink-0 mt-0.5" />
                <span>Canadian Solar – Solar Panel (12 Years)</span>
              </li>
              <li className="flex gap-3 items-start">
                <Check size={18} className="text-solar-gold flex-shrink-0 mt-0.5" />
                <span>DEYE / SOLIS – Inverter (5 Years)</span>
              </li>
              {isHybrid && (
                <li className="flex gap-3 items-start">
                  <Check size={18} className="text-solar-gold flex-shrink-0 mt-0.5" />
                  <span>LiFePO₄ Battery (5 Years)</span>
                </li>
              )}
            </ul>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-border-light p-6 space-y-3">
          <div className="text-sm text-text-muted text-center">
            ✓ Transparent proposal • Clean cabling • Full documentation
          </div>
          <div className="flex gap-3 flex-col sm:flex-row">
            <a
              href="#contacts"
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-lg bg-card border border-border-light text-text-body font-bold text-center transition hover:border-accent-sun hover:text-accent-sun"
            >
              Contact Us
            </a>
            <a
              href="#contacts"
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-lg bg-accent-sun hover:bg-orange-600 text-white font-bold text-center transition"
            >
              Request Quote
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}

/* --------------------------------- PAGE --------------------------------- */
export default function Products() {
  const CATEGORIES = useMemo(
    () => [
      { id: "grid", label: "Grid-Tie", data: GRID_TIE },
      { id: "h100", label: "Hybrid 100Ah", data: HYBRID_100 },
      { id: "h200", label: "Hybrid 200Ah", data: HYBRID_200 },
      { id: "h314", label: "Hybrid 314Ah", data: HYBRID_314 },
    ],
    []
  );

  const [activeTab, setActiveTab] = useState(0);
  const [activePkg, setActivePkg] = useState(null);
  const [productOpen, setProductOpen] = useState(false);

  const currentData = CATEGORIES[activeTab].data;

  const openProduct = (pkg) => {
    setActivePkg(pkg);
    setProductOpen(true);
  };

  return (
    <section id="products" className="relative w-full bg-base text-text-primary py-20 md:py-28 font-sora">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-base" />
      </div>

      {/* Content */}
      <div className="max-w-[92rem] mx-auto px-5 sm:px-8 md:px-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="font-sharetech text-4xl md:text-5xl font-bold mb-2 text-text-primary">Solar Packages</h2>
          <div className="h-1 w-32 bg-accent-sun/40 rounded-full mx-auto mb-4" />
          <p className="text-lg text-text-body max-w-2xl mx-auto">
            Premium solar systems engineered for Filipino homes. Choose grid-tie or hybrid with battery backup.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-12"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          viewport={{ once: true }}
        >
          {CATEGORIES.map((cat, idx) => (
            <button
              key={cat.id}
              onClick={() => setActiveTab(idx)}
              className={`px-6 py-2.5 rounded-full font-medium transition ${
                activeTab === idx
                  ? "bg-accent-sun text-white font-bold shadow-md"
                  : "bg-card text-text-body hover:border-accent-sun border border-border-light"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </motion.div>

        {/* Products Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          viewport={{ once: true }}
        >
          <AnimatePresence mode="wait">
            {currentData.map((pkg, idx) => (
              <motion.button
                key={`${pkg.kw}-${pkg.battery || 'no-battery'}`}
                onClick={() => openProduct(pkg)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: idx * 0.05, duration: 0.4 }}
                className="group relative h-full text-left"
              >
                <div
                  className={`h-full rounded-xl border-2 transition duration-300 p-6 space-y-4 ${
                    pkg.popular
                      ? "border-accent-sun bg-accent-sun/10 shadow-md scale-105"
                      : "border-border-light bg-card hover:border-accent-sun/50 hover:shadow-md"
                  }`}
                >
                  {/* Popular badge */}
                  {pkg.popular && (
                    <div className="absolute top-3 right-3 rounded-full bg-accent-sun text-white px-3 py-1 text-xs font-bold uppercase">
                      Most Popular
                    </div>
                  )}

                  {/* System size */}
                  <div>
                    <div className="font-sharetech text-3xl font-bold text-text-primary mb-1">{pkg.kw}</div>
                    <div className="text-sm text-text-muted uppercase tracking-wide">
                      {pkg.tag} {pkg.battery && `• ${pkg.battery}`}
                    </div>
                  </div>

                  {/* Price */}
                  <div className="py-4 border-t border-border-light">
                    <div className="font-sharetech text-2xl font-bold text-accent-sun">
                      {pkg.price}
                    </div>
                  </div>

                  {/* Quick specs */}
                  <div className="space-y-2 text-sm text-text-muted">
                    <div>✓ {pkg.inclusions[0]?.split("pcs")[0].trim()} Panels</div>
                    <div>✓ {pkg.inclusions[1]?.split("Inverter")[0].trim() || pkg.kw} Inverter</div>
                    {pkg.battery && <div>✓ {pkg.battery} Battery</div>}
                  </div>

                  {/* CTA */}
                  <div className="pt-4 mt-4 border-t border-border-light">
                    <span className="inline-flex items-center gap-1 text-accent-sun font-bold text-sm group-hover:gap-2 transition">
                      View Details <span>→</span>
                    </span>
                  </div>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Compare CTA */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-white/70 mb-4">
            Need help choosing? Use our solar calculator to get a personalized recommendation.
          </p>
          <a
            href="/#calculator"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white font-bold ring-1 ring-white/20 transition"
          >
            <span>Calculate Your Savings</span>
            <span>→</span>
          </a>
        </motion.div>
      </div>

      <ProductModal
        open={productOpen}
        onClose={() => setProductOpen(false)}
        pkg={activePkg}
      />
    </section>
  );
}
